import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  Fab,
} from '@mui/material';
import {
  Calculate,
  ExpandMore,
  Info,
  Warning,
  CheckCircle,
  Error as ErrorIcon,
  ContentCopy,
  Add,
  Agriculture,
  WaterDrop,
  AttachMoney,
  Scale,
  Timeline,
  Assessment,
  TrendingUp,
  Security,
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import api from 'services/api';

const SPECIES_OPTIONS = [
  { value: 'P_VANNAMEI', label: 'Penaeus vannamei (Whiteleg Shrimp)' },
  { value: 'P_INDICUS', label: 'Penaeus indicus (Indian White Shrimp)' },
  { value: 'P_MONODON', label: 'Penaeus monodon (Black Tiger Shrimp)' },
  { value: 'M_ROSENBERGII', label: 'Macrobrachium rosenbergii (Giant Freshwater Prawn)' },
];

const DOC_OPTIONS = [
  { value: 60, label: '60 Days' },
  { value: 80, label: '80 Days' },
  { value: 100, label: '100 Days' },
  { value: 120, label: '120 Days' },
  { value: 130, label: '130 Days' },
];

const MAX_STOCKING_DENSITY = {
  P_VANNAMEI: 60,
  P_INDICUS: 40,
  P_MONODON: 12,
  M_ROSENBERGII: 12,
};

const ClaimCalculator = () => {
  const { user } = useAuth();
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    species: 'P_VANNAMEI',
    doc_chosen: 120,
    day_of_loss: '',
    actual_stocking_density_per_m2: '',
    use_declared_input_cost: false,
    declared_input_cost: '',
    declared_fixed_inputs: '',
    market_price_per_kg: '',
    total_sellable_weight_kg: '',
    sum_insured_choice: 'STANDARD',
    waiting_period_days: 7,
  });

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.day_of_loss || formData.day_of_loss < 1 || formData.day_of_loss > formData.doc_chosen) {
      errors.day_of_loss = `Must be between 1 and ${formData.doc_chosen}`;
    }
    
    if (!formData.actual_stocking_density_per_m2 || formData.actual_stocking_density_per_m2 <= 0) {
      errors.actual_stocking_density_per_m2 = 'Must be greater than 0';
    }
    
    if (formData.use_declared_input_cost) {
      if (!formData.declared_input_cost || formData.declared_input_cost <= 0) {
        errors.declared_input_cost = 'Required when using declared input cost';
      }
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCalculate = async () => {
    if (!validateForm()) {
      setError('Please fix the validation errors before calculating');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);
    setWarnings([]);

    try {
      const requestData = {
        species: formData.species,
        doc_chosen: formData.doc_chosen,
        day_of_loss: parseInt(formData.day_of_loss),
        actual_stocking_density_per_m2: parseFloat(formData.actual_stocking_density_per_m2),
        use_declared_input_cost: formData.use_declared_input_cost,
        waiting_period_days: parseInt(formData.waiting_period_days),
      };

      if (formData.use_declared_input_cost && formData.declared_input_cost) {
        requestData.declared_input_cost = parseFloat(formData.declared_input_cost);
      }
      if (formData.declared_fixed_inputs) {
        requestData.declared_fixed_inputs = parseFloat(formData.declared_fixed_inputs);
      }
      if (formData.market_price_per_kg) {
        requestData.declared_market_price_per_kg = parseFloat(formData.market_price_per_kg);
      }
      if (formData.total_sellable_weight_kg) {
        requestData.total_sellable_weight_kg = parseFloat(formData.total_sellable_weight_kg);
      }

      const response = await api.post('/api/v1/calculate-claim', requestData);
      
      if (response.data.success) {
        setResult(response.data.data);
        setOpenDialog(false); // Close form dialog after successful calculation
      } else {
        setError(response.data.error?.message || 'Calculation failed');
      }
    } catch (err) {
      console.error('Calculation error:', err);
      setError(err.response?.data?.detail || 'Failed to calculate claim. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setError('');
    setResult(null);
    setWarnings([]);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
    setFieldErrors({});
  };

  const getSpeciesInfo = (species) => {
    const info = {
      P_VANNAMEI: { maxDensity: 60, description: 'Whiteleg Shrimp' },
      P_INDICUS: { maxDensity: 40, description: 'Indian White Shrimp' },
      P_MONODON: { maxDensity: 12, description: 'Black Tiger Shrimp' },
      M_ROSENBERGII: { maxDensity: 12, description: 'Giant Freshwater Prawn' },
    };
    return info[species] || { maxDensity: 50, description: 'Unknown Species' };
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Paper elevation={3} sx={{ 
        p: 4, 
        mb: 4, 
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        borderRadius: 2,
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
      }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Box
            sx={{
              p: 2,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.2)',
              mr: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Assessment sx={{ fontSize: 32, color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h4" component="h1" color="white" fontWeight="bold" gutterBottom>
              Shrimp Insurance Claim Calculator
            </Typography>
            <Typography variant="h6" color="white" sx={{ opacity: 0.9, fontWeight: 400 }}>
              Calculate claim payouts with policy-specific validations (70% threshold, 80% indemnity rate)
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Quick Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            textAlign: 'center', 
            p: 3, 
            height: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }
          }}>
            <Box sx={{ 
              p: 2, 
              borderRadius: '50%', 
              backgroundColor: 'primary.50', 
              display: 'inline-flex',
              mb: 2
            }}>
              <Agriculture color="primary" sx={{ fontSize: 32 }} />
            </Box>
            <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
              4 Species
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Supported
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            textAlign: 'center', 
            p: 3, 
            height: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }
          }}>
            <Box sx={{ 
              p: 2, 
              borderRadius: '50%', 
              backgroundColor: 'success.50', 
              display: 'inline-flex',
              mb: 2
            }}>
              <Timeline color="success" sx={{ fontSize: 32 }} />
            </Box>
            <Typography variant="h5" fontWeight="bold" color="success.main" gutterBottom>
              60-130 Days
            </Typography>
            <Typography variant="body2" color="text.secondary">
              DOC Coverage
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            textAlign: 'center', 
            p: 3, 
            height: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }
          }}>
            <Box sx={{ 
              p: 2, 
              borderRadius: '50%', 
              backgroundColor: 'warning.50', 
              display: 'inline-flex',
              mb: 2
            }}>
              <AttachMoney color="warning" sx={{ fontSize: 32 }} />
            </Box>
            <Typography variant="h5" fontWeight="bold" color="warning.main" gutterBottom>
              ₹9L-18L
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sum Insured Range
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            textAlign: 'center', 
            p: 3, 
            height: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }
          }}>
            <Box sx={{ 
              p: 2, 
              borderRadius: '50%', 
              backgroundColor: 'info.50', 
              display: 'inline-flex',
              mb: 2
            }}>
              <Scale color="info" sx={{ fontSize: 32 }} />
            </Box>
            <Typography variant="h5" fontWeight="bold" color="info.main" gutterBottom>
              20%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Standard Deductible
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Calculator Form */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Calculate sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Calculate Your Claim
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Enter your loss parameters to get an accurate claim calculation based on AIC India policy rules.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleOpenDialog}
                  startIcon={<Calculate />}
                  sx={{ minWidth: 200 }}
                >
                  Start Calculation
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* How It Works */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Info color="primary" />
                How It Works
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Chip label="1" color="primary" size="small" />
                  <Box>
                    <Typography variant="subtitle2">Enter Loss Details</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Provide species, DOC period, day of loss, and stocking density
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Chip label="2" color="primary" size="small" />
                  <Box>
                    <Typography variant="subtitle2">Calculate Sum Insured</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Use standard SI table or declare your actual input costs
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Chip label="3" color="primary" size="small" />
                  <Box>
                    <Typography variant="subtitle2">Apply Policy Rules</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Fixed costs, variable costs, salvage, and deductible calculations
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Chip label="4" color="primary" size="small" />
                  <Box>
                    <Typography variant="subtitle2">Get Final Payout</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Detailed breakdown with all calculation steps and audit trail
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Calculation Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Calculate color="primary" />
            <Box>
              <Typography variant="h6">Claim Calculation</Typography>
              <Typography variant="body2" color="text.secondary">
                Enter your loss parameters for accurate claim calculation
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        
        <form onSubmit={(e) => { e.preventDefault(); handleCalculate(); }}>
          <DialogContent sx={{ pt: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Basic Details Row */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth required>
                  <InputLabel>Species</InputLabel>
                  <Select
                    value={formData.species}
                    onChange={(e) => handleInputChange('species', e.target.value)}
                    label="Species"
                  >
                    {SPECIES_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth required>
                  <InputLabel>DOC Period</InputLabel>
                  <Select
                    value={formData.doc_chosen}
                    onChange={(e) => handleInputChange('doc_chosen', e.target.value)}
                    label="DOC Period"
                  >
                    {DOC_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {formData.species && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>{getSpeciesInfo(formData.species).description}</strong> - 
                    Max recommended stocking density: {getSpeciesInfo(formData.species).maxDensity}/m²
                  </Typography>
                </Alert>
              )}

              {/* Loss Details Row */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Day of Loss"
                  type="number"
                  value={formData.day_of_loss}
                  onChange={(e) => handleInputChange('day_of_loss', e.target.value)}
                  required
                  error={!!fieldErrors.day_of_loss}
                  helperText={fieldErrors.day_of_loss || `Must be between 1 and ${formData.doc_chosen}`}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">Day</InputAdornment>,
                  }}
                />
                <TextField
                  fullWidth
                  label="Stocking Density"
                  type="number"
                  value={formData.actual_stocking_density_per_m2}
                  onChange={(e) => handleInputChange('actual_stocking_density_per_m2', e.target.value)}
                  required
                  error={!!fieldErrors.actual_stocking_density_per_m2}
                  helperText={fieldErrors.actual_stocking_density_per_m2 || "Per square meter"}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">/m²</InputAdornment>,
                  }}
                />
              </Box>

              {/* Waiting Period */}
              <TextField
                fullWidth
                label="Waiting Period"
                type="number"
                value={formData.waiting_period_days}
                onChange={(e) => handleInputChange('waiting_period_days', e.target.value)}
                helperText="Default: 7 days (total loss within waiting period is not covered)"
                InputProps={{
                  endAdornment: <InputAdornment position="end">days</InputAdornment>,
                }}
              />

              <Divider sx={{ my: 2 }} />

              {/* Sum Insured Options */}
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoney color="primary" />
                Sum Insured Options
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.use_declared_input_cost}
                    onChange={(e) => handleInputChange('use_declared_input_cost', e.target.checked)}
                  />
                }
                label="Use Declared Input Cost (instead of standard SI table)"
                sx={{ mb: 2 }}
              />

              {formData.use_declared_input_cost && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Declared Input Cost"
                    type="number"
                    value={formData.declared_input_cost}
                    onChange={(e) => handleInputChange('declared_input_cost', e.target.value)}
                    required={formData.use_declared_input_cost}
                    error={!!fieldErrors.declared_input_cost}
                    helperText={fieldErrors.declared_input_cost || "Your actual input cost"}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Declared Fixed Inputs"
                    type="number"
                    value={formData.declared_fixed_inputs}
                    onChange={(e) => handleInputChange('declared_fixed_inputs', e.target.value)}
                    helperText="Optional: actual fixed input cost"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Evidence-Based Salvage */}
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Scale color="primary" />
                Evidence-Based Salvage (Optional)
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Market Price per kg"
                  type="number"
                  value={formData.market_price_per_kg}
                  onChange={(e) => handleInputChange('market_price_per_kg', e.target.value)}
                  helperText="For evidence-based salvage calculation"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    endAdornment: <InputAdornment position="end">/kg</InputAdornment>,
                  }}
                />
                <TextField
                  fullWidth
                  label="Total Sellable Weight"
                  type="number"
                  value={formData.total_sellable_weight_kg}
                  onChange={(e) => handleInputChange('total_sellable_weight_kg', e.target.value)}
                  helperText="For evidence-based salvage calculation"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                  }}
                />
              </Box>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
            <Button 
              onClick={handleCloseDialog}
              variant="outlined"
              sx={{ minWidth: 100 }}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Calculate />}
              sx={{ minWidth: 150 }}
            >
              {loading ? 'Calculating...' : 'Calculate Claim'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Results Section */}
      {result && (
        <Box sx={{ mt: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircle color="success" />
                Calculation Results
              </Typography>
              
              {!result.admissible && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="h6">Claim Not Admissible</Typography>
                  <Typography>{result.reason}</Typography>
                </Alert>
              )}

              {result.admissible && (
                <>
                  {/* Policy Validation Information */}
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Policy Validation Results
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Total Loss Threshold:</strong> {result.total_loss_threshold || 70}% minimum required
                        </Typography>
                        <Typography variant="body2">
                          <strong>Loss Percentage:</strong> {result.loss_percentage || 100}%
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Indemnity Rate:</strong> {result.indemnity_rate || 80}% of sum insured
                        </Typography>
                        <Typography variant="body2">
                          <strong>Indemnity Payout:</strong> ₹{result.indemnity_payout ? parseFloat(result.indemnity_payout).toFixed(2) : 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Alert>

                  {/* Summary Cards */}
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.50' }}>
                        <AttachMoney color="primary" sx={{ fontSize: 30, mb: 1 }} />
                        <Typography variant="h6" color="primary">
                          {formatCurrency(result.sum_insured)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Sum Insured
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'success.50' }}>
                        <TrendingUp color="success" sx={{ fontSize: 30, mb: 1 }} />
                        <Typography variant="h6" color="success.main">
                          {formatCurrency(result.gross_payout_before_deductions)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Gross Payout
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.50' }}>
                        <Scale color="warning" sx={{ fontSize: 30, mb: 1 }} />
                        <Typography variant="h6" color="warning.main">
                          {formatCurrency(result.salvage)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Salvage Deduction
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.50' }}>
                        <Scale color="warning" sx={{ fontSize: 30, mb: 1 }} />
                        <Typography variant="h6" color="warning.main">
                          {formatCurrency(result.indemnity_payout || 0)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Indemnity Payout ({result.indemnity_rate || 80}%)
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'success.50' }}>
                        <Security color="success" sx={{ fontSize: 30, mb: 1 }} />
                        <Typography variant="h6" color="success.main">
                          {formatCurrency(result.final_payout)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Final Payout
                        </Typography>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Detailed Breakdown */}
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="h6">Detailed Calculation Breakdown</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer component={Paper} variant="outlined">
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Component</strong></TableCell>
                              <TableCell align="right"><strong>Amount (₹)</strong></TableCell>
                              <TableCell><strong>Description</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>Sum Insured</TableCell>
                              <TableCell align="right">{formatCurrency(result.sum_insured)}</TableCell>
                              <TableCell>Base insurance coverage amount</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Fixed Cost</TableCell>
                              <TableCell align="right">{formatCurrency(result.fixed_cost)}</TableCell>
                              <TableCell>Fixed cost component of the claim</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Variable Cost</TableCell>
                              <TableCell align="right">{formatCurrency(result.variable_cost)}</TableCell>
                              <TableCell>Variable cost component based on day of loss</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Gross Payout</TableCell>
                              <TableCell align="right">{formatCurrency(result.gross_payout_before_deductions)}</TableCell>
                              <TableCell>Total before deductions</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Salvage Deduction</TableCell>
                              <TableCell align="right">-{formatCurrency(result.salvage)}</TableCell>
                              <TableCell>Salvage value deducted from claim</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Deductible ({result.deductible_percent}%)</TableCell>
                              <TableCell align="right">-{formatCurrency((result.gross_payout_before_deductions - result.salvage) * (result.deductible_percent / 100))}</TableCell>
                              <TableCell>Standard policy deductible</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Indemnity Rate ({result.indemnity_rate || 80}%)</TableCell>
                              <TableCell align="right">{formatCurrency(result.indemnity_payout || 0)}</TableCell>
                              <TableCell>Policy indemnity rate applied</TableCell>
                            </TableRow>
                            <TableRow sx={{ bgcolor: 'success.50' }}>
                              <TableCell><strong>Final Payout</strong></TableCell>
                              <TableCell align="right"><strong>{formatCurrency(result.final_payout)}</strong></TableCell>
                              <TableCell><strong>Amount payable to farmer</strong></TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>

                  {/* Audit Log */}
                  {result.audit && result.audit.length > 0 && (
                    <Accordion sx={{ mt: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="h6">Calculation Audit Trail</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {result.audit.map((log, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Chip label={index + 1} size="small" color="primary" />
                              <Typography variant="body2">{log}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Applied Caps */}
                  {result.applied_caps && (
                    <Accordion sx={{ mt: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="h6">Applied Policy Caps</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2">
                              <strong>Max Allowed Percentage:</strong> {result.applied_caps.max_allowed_pct}%
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2">
                              <strong>Max Allowed Amount:</strong> {formatCurrency(result.applied_caps.max_allowed_amount)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default ClaimCalculator;